import { useState } from 'react';
import { useSetRecoilState } from 'recoil';

import { eventsSnackBarAtom, useAuthContext, useSyncAppleReminders } from '../data-access';
import {
  fetchAppleRemindersFromDesktop,
  isAppleRemindersDesktopSupported,
  openAppleRemindersSettings,
} from '../helpers';
import { trackEventMixpanel } from '../utils';

export const useConnectAppleReminders = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuthContext();
  const setEventsSnackbar = useSetRecoilState(eventsSnackBarAtom);
  const { syncAppleReminders } = useSyncAppleReminders();

  const connectAppleReminders = async (): Promise<boolean> => {
    try {
      setError('');
      setIsLoading(true);

      if (!isAppleRemindersDesktopSupported()) {
        throw new Error('Apple Reminders native sync is only available in the macOS desktop app.');
      }

      const { lists, reminders } = await fetchAppleRemindersFromDesktop();

      await syncAppleReminders({
        userId: user.userId,
        metricId: user.metricId,
        userPersona: user.persona?.job,
        lists,
        reminders,
      });

      setEventsSnackbar({
        message: `Apple Reminders synced (${lists.length} lists, ${reminders.length} reminders).`,
        autoHideDuration: 3000,
        type: 'success',
      });

      setIsLoading(false);
      trackEventMixpanel('connect_apple_reminders');
      return true;
    } catch (err) {
      setIsLoading(false);
      const rawErrorMessage =
        (err as Error)?.message || 'Something went wrong connecting Apple Reminders.';
      const isPermissionIssue =
        rawErrorMessage.includes('reminder_permission_denied') ||
        rawErrorMessage.includes('reminders_permission_denied') ||
        rawErrorMessage.toLowerCase().includes('permission');
      const canOpenSettings = isPermissionIssue && isAppleRemindersDesktopSupported();
      const snackMessage = isPermissionIssue
        ? 'Apple Reminders permission denied. Enable Reminders access in macOS Settings and try again.'
        : rawErrorMessage;

      setError(rawErrorMessage);
      setEventsSnackbar({
        message: snackMessage,
        autoHideDuration: 6000,
        type: 'error',
        ...(canOpenSettings
          ? {
              actionLabel: 'Open Settings',
              onPress: () => {
                void (async () => {
                  const opened = await openAppleRemindersSettings();
                  if (!opened) {
                    setEventsSnackbar({
                      message:
                        'Open System Settings > Privacy & Security > Reminders and enable access for Lifestack.',
                      autoHideDuration: 6000,
                      type: 'error',
                    });
                  }
                })();
              },
            }
          : {}),
      });
      return false;
    }
  };

  return { connectAppleReminders, isLoading, error };
};
